import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogBySlug, likeBlog, unlikeBlog, getPublicBlogs, subscribeNewsletter } from '../../utils/api';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import { toast } from 'sonner';
import { getBlogComments, postBlogComment, adminDeleteBlogComment, getCurrentUser } from '../../utils/api';
import { getAuthToken } from '../../utils/cookies';
import { sanitizeHTML } from '../../utils/sanitize';

// ── Utility: Build Comment Tree ───────────────────────────────────
const buildCommentTree = (flatComments) => {
    if (!flatComments) return [];
    const map = {};
    const roots = [];

    flatComments.forEach(c => {
        map[c.id] = { ...c, children: [] };
    });

    flatComments.forEach(c => {
        if (c.parent_id && map[c.parent_id]) {
            map[c.parent_id].children.push(map[c.id]);
        } else {
            roots.push(map[c.id]);
        }
    });

    return roots;
};

// ── Sub-component: Comment Item ──────────────────────────────────
const CommentItem = ({
    comment,
    depth = 0,
    currentUser,
    activeReplyId,
    setActiveReplyId,
    replyContent,
    setReplyContent,
    isSubmittingReply,
    handleSubmitComment,
    handleDeleteComment
}) => {
    const isReplying = activeReplyId === comment.id;

    return (
        <div className={`flex flex-col gap-3 py-4 ${depth > 0 ? 'ml-6 border-l border-slate-100 pl-4' : 'border-b border-slate-100'}`}>
            <div className="flex gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold shrink-0">
                    {comment.author_name?.charAt(0) || 'U'}
                </span>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-gray-900">{comment.author_name}</span>
                        <span className="text-[10px] text-gray-400">
                            {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {comment.content}
                    </p>

                    <div className="flex items-center gap-4 mt-2">
                        {currentUser && (
                            <button
                                onClick={() => setActiveReplyId(isReplying ? null : comment.id)}
                                className="text-[10px] font-boldtracking-wider text-gray-400 hover:text-emerald-500 transition-colors flex items-center gap-1"
                            >
                                {isReplying ? 'Cancel' : 'Reply'}
                            </button>
                        )}
                        {currentUser?.role === 'Admin' && (
                            <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-[10px] font-boldtracking-wider text-gray-400 hover:text-rose-500 transition-colors"
                            >
                                Delete
                            </button>
                        )}
                    </div>

                    {isReplying && (
                        <div className="mt-3 bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
                            <textarea
                                value={replyContent[comment.id] || ''}
                                onChange={(e) => setReplyContent(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                placeholder="Write your reply..."
                                className="w-full h-20 p-3 bg-white border border-gray-200 rounded-lg focus:border-emerald-400 transition-all text-sm text-gray-900 resize-none outline-none mb-2"
                                autoFocus
                            />
                            <div className="flex justify-end">
                                <button
                                    onClick={() => handleSubmitComment(null, comment.id)}
                                    disabled={isSubmittingReply[comment.id] || !(replyContent[comment.id] || '').trim()}
                                    className="px-4 py-1.5 bg-emerald-500 text-white text-[10px] font-boldtracking-wider rounded transition disabled:opacity-50"
                                >
                                    {isSubmittingReply[comment.id] ? 'Posting...' : 'Post Reply'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Render children recursively */}
            {comment.children && comment.children.length > 0 && (
                <div className="mt-1">
                    {comment.children.map(child => (
                        <CommentItem
                            key={child.id}
                            comment={child}
                            depth={depth + 1}
                            currentUser={currentUser}
                            activeReplyId={activeReplyId}
                            setActiveReplyId={setActiveReplyId}
                            replyContent={replyContent}
                            setReplyContent={setReplyContent}
                            isSubmittingReply={isSubmittingReply}
                            handleSubmitComment={handleSubmitComment}
                            handleDeleteComment={handleDeleteComment}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [interactionState, setInteractionState] = useState({ liked: false, unliked: false });
    const [likesCount, setLikesCount] = useState(0);
    const [unlikesCount, setUnlikesCount] = useState(0);
    const [commentText, setCommentText] = useState('');
    const [comments, setComments] = useState([]);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [activeReplyId, setActiveReplyId] = useState(null);
    const [replyContent, setReplyContent] = useState({});
    const [isSubmittingReply, setIsSubmittingReply] = useState({});
    const [readProgress, setReadProgress] = useState(0);
    const [bookmarked, setBookmarked] = useState(false);
    const articleRef = useRef(null);

    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [submittingNewsletter, setSubmittingNewsletter] = useState(false);
    const [subscribeStatus, setSubscribeStatus] = useState(null);

    // Reading progress bar
    useEffect(() => {
        const handleScroll = () => {
            const el = articleRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const totalHeight = el.scrollHeight;
            const scrolled = Math.max(0, -rect.top);
            const progress = Math.min(100, Math.max(0, (scrolled / (totalHeight - window.innerHeight)) * 100));
            setReadProgress(progress);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [blog]);

    // Bookmark from localStorage
    useEffect(() => {
        if (slug) {
            const saved = JSON.parse(localStorage.getItem('blog_bookmarks') || '[]');
            setBookmarked(saved.includes(slug));
        }
    }, [slug]);

    const toggleBookmark = useCallback(() => {
        const saved = JSON.parse(localStorage.getItem('blog_bookmarks') || '[]');
        let updated;
        if (saved.includes(slug)) {
            updated = saved.filter(s => s !== slug);
            toast.success('Removed from bookmarks');
        } else {
            updated = [...saved, slug];
            toast.success('Bookmarked!');
        }
        localStorage.setItem('blog_bookmarks', JSON.stringify(updated));
        setBookmarked(updated.includes(slug));
    }, [slug]);

    // Record Telemetry
    useEffect(() => {
        if (slug) {
            // Add a small delay so it doesn't block critical rendering
            const timer = setTimeout(() => {
                api.post(`/api/blogs/${slug}/telemetry`, {
                    referer: document.referrer || '',
                    device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
                }).catch(() => { });
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [slug]);

    const handleNewsletterSubscribe = async (e) => {
        e.preventDefault();
        if (!newsletterEmail) return;
        setSubmittingNewsletter(true);
        try {
            await subscribeNewsletter(newsletterEmail);
            setSubscribeStatus('success');
            setNewsletterEmail('');
            toast.success("Subscribed to newsletter!");
            setTimeout(() => setSubscribeStatus(null), 5000);
        } catch (error) {
            console.error("Newsletter subscription failed:", error);
            setSubscribeStatus('error');
            toast.error("Failed to subscribe to newsletter.");
            setTimeout(() => setSubscribeStatus(null), 5000);
        } finally {
            setSubmittingNewsletter(false);
        }
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = getAuthToken(); // Might be null if HttpOnly
                const userData = await getCurrentUser(token);
                setCurrentUser(userData);
            } catch (err) {
                // Not authenticated or error
                console.error("Auth check failed", err);
                setCurrentUser(null);
            }
        };
        checkAuth();
    }, []);

    const fetchComments = async (blogSlug) => {
        try {
            const data = await getBlogComments(blogSlug);
            setComments(data || []);
        } catch (error) {
            console.error("Failed to load comments", error);
        }
    };

    useEffect(() => {
        const fetchBlogData = async () => {
            try {
                const blogData = await getBlogBySlug(slug);
                setBlog(blogData);
                setLikesCount(blogData.likes_count || 0);
                setUnlikesCount(blogData.unlikes_count || 0);

                fetchComments(slug);

                const allBlogs = await getPublicBlogs();
                const others = allBlogs.filter(b => b.id !== blogData.id).slice(0, 3);
                setLatestBlogs(others);
            } catch (error) {
                toast.error("Blog post not found");
                navigate('/blogs');
            } finally {
                setLoading(false);
            }
        };
        fetchBlogData();
        setInteractionState({ liked: false, unliked: false });
    }, [slug, navigate]);

    const handleLike = async () => {
        if (interactionState.liked) return;
        try {
            await likeBlog(slug);
            setLikesCount(prev => prev + 1);
            setInteractionState(prev => ({ ...prev, liked: true }));
        } catch (error) {
            toast.error("Failed to like post");
        }
    };

    const handleUnlike = async () => {
        if (interactionState.unliked) return;
        try {
            await unlikeBlog(slug);
            setUnlikesCount(prev => prev + 1);
            setInteractionState(prev => ({ ...prev, unliked: true }));
        } catch (error) {
            toast.error("Failed to unlike post");
        }
    };

    const handleSubmitComment = async (e, parentId = null) => {
        if (e) e.preventDefault();
        const content = parentId ? replyContent[parentId] : commentText;

        if (!currentUser) {
            toast.error('Please log in to comment.');
            return;
        }

        if (!content?.trim()) {
            toast.error("Comment cannot be empty.");
            return;
        }

        if (parentId) {
            setIsSubmittingReply(prev => ({ ...prev, [parentId]: true }));
        } else {
            setSubmittingComment(true);
        }

        try {
            await postBlogComment(slug, content, parentId);
            toast.success('Comment posted!');
            if (parentId) {
                setReplyContent(prev => ({ ...prev, [parentId]: '' }));
                setActiveReplyId(null);
            } else {
                setCommentText('');
            }
            fetchComments(slug);
        } catch (error) {
            const errorMsg = error.error || error.message || 'Failed to post comment.';
            toast.error(errorMsg);
            if (error.reason) {
                toast.error(`Moderation reason: ${error.reason}`, { duration: 5000 });
            }
        } finally {
            if (parentId) {
                setIsSubmittingReply(prev => ({ ...prev, [parentId]: false }));
            } else {
                setSubmittingComment(false);
            }
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) return;
        try {
            await adminDeleteBlogComment(commentId);
            toast.success("Comment deleted.");
            fetchComments(slug);
        } catch (error) {
            toast.error(error?.error || "Failed to delete comment.");
        }
    };

    const commentTree = useMemo(() => buildCommentTree(comments), [comments]);

    // Sanitize + inject IDs into headings for TOC navigation
    const sanitizedContentWithIds = useMemo(() => {
        if (!blog?.content) return '';
        const clean = sanitizeHTML(blog.content);
        const parser = new DOMParser();
        const doc = parser.parseFromString(clean, 'text/html');
        const headings = doc.querySelectorAll('h1, h2, h3');
        headings.forEach((h, i) => {
            h.setAttribute('id', `heading-${i}`);
        });
        return doc.body.innerHTML;
    }, [blog?.content]);

    // Extract headings from content for TOC
    const tocItems = useMemo(() => {
        if (!blog?.content) return [];
        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitizeHTML(blog.content), 'text/html');
        const headings = doc.querySelectorAll('h1, h2, h3');
        return Array.from(headings).map((h, i) => ({
            id: `heading-${i}`,
            text: h.textContent,
            level: parseInt(h.tagName.charAt(1)),
        }));
    }, [blog?.content]);

    const scrollToHeading = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <NavigationHeader />
                <div className="max-w-4xl mx-auto px-6 pt-16 space-y-8 animate-pulse">
                    <div className="h-8 bg-slate-50 rounded w-1/3" />
                    <div className="h-12 bg-slate-50 rounded w-3/4" />
                    <div className="h-[400px] bg-slate-50 rounded-xl" />
                    <div className="space-y-4">
                        <div className="h-4 bg-slate-50 rounded w-full" />
                        <div className="h-4 bg-slate-50 rounded w-full" />
                        <div className="h-4 bg-slate-50 rounded w-5/6" />
                    </div>
                </div>
            </div>
        );
    }

    if (!blog) return null;

    const category = blog.tags?.[0] || 'Engineering';

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {/* Reading Progress Bar */}
            <div className="fixed top-0 left-0 w-full h-[3px] z-[60] bg-transparent">
                <div
                    className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 transition-all duration-150 ease-out"
                    style={{ width: `${readProgress}%` }}
                />
            </div>
            <NavigationHeader />

            {/* ── HERO: Cover Image ── */}
            {blog.cover_image_url && (
                <div className="w-full h-[400px] md:h-[550px] relative">
                    <img
                        src={blog.cover_image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-white via-white/90 to-transparent" />
                </div>
            )}


            {/* ── META + TITLE ── */}
            <div className={`max-w-[1300px] mx-auto px-6 lg:px-8 ${blog.cover_image_url ? '-mt-24 pb-12 relative z-10' : 'pt-24 pb-12'}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 lg:col-start-2">
                        <div className="mb-6">
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <span className="bg-emerald-500 text-white text-[10px] font-boldtracking-[0.15em] px-3 py-1 rounded">
                                    {category}
                                </span>
                                <span className="flex items-center gap-1.5 text-sm text-gray-400">
                                    <Icon name="Clock" size={14} className="text-emerald-500" />
                                    {blog.read_time_minutes || 5} min read
                                </span>
                                <span className="text-sm text-gray-400">·</span>
                                <span className="text-sm text-gray-400">
                                    {new Date(blog.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            </div>

                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight mb-6 text-gray-900">
                                {blog.title}
                            </h1>

                            {/* Author */}
                            {blog.author_full_name && (
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm font-bold border-2 border-white shadow-sm">
                                        {blog.author_full_name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-900">{blog.author_full_name}</p>
                                        <p className="text-[10px] font-boldtracking-wider text-emerald-600">prepxai Contributor</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── CONTENT AREA ── */}
            <div className="max-w-[1300px] mx-auto px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left: Floating Action Bar */}
                    <div className="hidden lg:block lg:col-span-1">
                        <div className="sticky top-20 flex flex-col items-center gap-4">
                            <button
                                onClick={handleLike}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${interactionState.liked ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'border-gray-200 text-gray-400 hover:border-emerald-300 hover:text-emerald-500'}`}
                                title="Like"
                            >
                                <Icon name="ThumbsUp" size={16} className={interactionState.liked ? 'fill-emerald-500' : ''} />
                            </button>
                            <span className="text-xs font-bold text-gray-400">{likesCount}</span>

                            <button
                                onClick={handleUnlike}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${interactionState.unliked ? 'bg-rose-50 border-rose-300 text-rose-600' : 'border-gray-200 text-gray-400 hover:border-rose-300 hover:text-rose-500'}`}
                                title="Dislike"
                            >
                                <Icon name="ThumbsDown" size={16} className={interactionState.unliked ? 'fill-rose-500' : ''} />
                            </button>

                            <button
                                onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:border-emerald-300 hover:text-emerald-500 transition-all"
                                title="Share"
                            >
                                <Icon name="Share2" size={16} />
                            </button>

                            <button
                                onClick={toggleBookmark}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${bookmarked ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'border-gray-200 text-gray-400 hover:border-emerald-300 hover:text-emerald-500'}`}
                                title={bookmarked ? 'Remove bookmark' : 'Bookmark'}
                            >
                                <Icon name="Bookmark" size={16} className={bookmarked ? 'fill-emerald-500' : ''} />
                            </button>
                        </div>
                    </div>

                    {/* Center: Article */}
                    <div className="lg:col-span-8" ref={articleRef}>
                        {/* Tags */}
                        {blog.tags && blog.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-6">
                                {blog.tags.map((tag, i) => (
                                    <span key={i} className="text-[10px] font-boldtracking-[0.15em] text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded">#{tag}</span>
                                ))}
                            </div>
                        )}

                        {/* Summary quote */}
                        {blog.summary && (
                            <div className="border-l-4 border-emerald-500 pl-5 py-2 mb-10">
                                <p className="text-gray-600 italic leading-relaxed">{blog.summary}</p>
                            </div>
                        )}

                        {/* Article content — sanitized with heading IDs */}
                        <article className="prose prose-gray prose-lg max-w-none
                            prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-gray-900
                            prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline
                            prose-img:rounded-2xl
                            prose-blockquote:border-l-emerald-500 prose-blockquote:bg-slate-50 prose-blockquote:py-1 prose-blockquote:px-5 prose-blockquote:rounded-r-xl prose-blockquote:not-italic
                            prose-code:bg-slate-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-medium
                            prose-pre:bg-slate-900 prose-pre:rounded-2xl prose-pre:text-sm"
                        >
                            <div dangerouslySetInnerHTML={{ __html: sanitizedContentWithIds }} />
                        </article>

                        {/* Mobile actions */}
                        <div className="flex items-center gap-3 mt-10 mb-10 lg:hidden">
                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${interactionState.liked ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-emerald-300'}`}
                            >
                                <Icon name="ThumbsUp" size={16} /> {likesCount}
                            </button>
                            <button
                                onClick={handleUnlike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${interactionState.unliked ? 'bg-rose-50 border-rose-300 text-rose-700' : 'border-gray-200 text-gray-600 hover:border-rose-300'}`}
                            >
                                <Icon name="ThumbsDown" size={16} /> {unlikesCount}
                            </button>
                            <button
                                onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }}
                                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-gray-200 text-gray-600 hover:border-emerald-300"
                            >
                                <Icon name="Share2" size={16} /> Share
                            </button>
                        </div>

                        {/* Comments Section */}
                        <div className="border-t border-gray-200 pt-10">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 font-display">
                                Discussion ({comments.length})
                            </h2>

                            {/* New Comment Input */}
                            <div className="flex items-start gap-3 mb-10">
                                <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                    {currentUser?.full_name?.charAt(0) || <Icon name="User" size={18} />}
                                </span>
                                <div className="flex-1 relative">
                                    {!currentUser && (
                                        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl p-4 text-center">
                                            <p className="text-sm font-medium text-gray-600">
                                                Please <Link to="/auth/login" className="text-emerald-600 font-bold hover:underline">login</Link> to join the discussion.
                                            </p>
                                        </div>
                                    )}
                                    <textarea
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        placeholder="What are your thoughts on this article?"
                                        rows={4}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 transition-all resize-none shadow-sm"
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            onClick={handleSubmitComment}
                                            disabled={submittingComment || !commentText.trim() || !currentUser}
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-boldtracking-[0.1em] px-6 py-2.5 rounded-lg transition-all shadow-md active:scale-95 disabled:opacity-40"
                                        >
                                            {submittingComment ? 'Posting...' : 'Post Comment'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Comments List */}
                            <div className="space-y-2">
                                {commentTree.length === 0 ? (
                                    <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
                                        <Icon name="MessageSquare" size={32} className="mx-auto text-slate-200 mb-4" />
                                        <p className="text-sm text-slate-400 font-boldtracking-widest italic">Be the first to share your thoughts</p>
                                    </div>
                                ) : (
                                    commentTree.map(c => (
                                        <CommentItem
                                            key={c.id}
                                            comment={c}
                                            currentUser={currentUser}
                                            activeReplyId={activeReplyId}
                                            setActiveReplyId={setActiveReplyId}
                                            replyContent={replyContent}
                                            setReplyContent={setReplyContent}
                                            isSubmittingReply={isSubmittingReply}
                                            handleSubmitComment={handleSubmitComment}
                                            handleDeleteComment={handleDeleteComment}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Sidebar */}
                    <aside className="lg:col-span-3">
                        <div className="sticky top-24 space-y-8 max-h-[calc(100vh-120px)] overflow-y-auto pr-2 custom-scrollbar-mini">
                            {/* Table of Contents */}
                            {tocItems.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-boldtracking-[0.2em] text-emerald-600 mb-4">In this article</h3>
                                    <div className="space-y-2.5">
                                        {tocItems.map((item, i) => (
                                            <p
                                                key={i}
                                                onClick={() => scrollToHeading(item.id)}
                                                className={`text-sm text-gray-500 hover:text-emerald-600 cursor-pointer transition-colors leading-snug ${item.level > 2 ? 'pl-4' : ''}`}
                                            >
                                                {item.text}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Related Articles in Sidebar */}
                            {latestBlogs.length > 0 && (
                                <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 shadow-sm">
                                    <h4 className="font-bold text-gray-900 mb-4 flex items-center justify-between">
                                        Related Deep Dives
                                        <Link to="/blogs" className="text-[10px] text-emerald-600 font-boldtracking-wider hover:text-emerald-700">View All</Link>
                                    </h4>
                                    <div className="space-y-4">
                                        {latestBlogs.slice(0, 3).map((post) => (
                                            <Link key={post.id} to={`/blogs/${post.slug}`} className="group flex gap-3 items-start">
                                                <div className="w-16 h-12 rounded bg-gray-200 flex-shrink-0 overflow-hidden">
                                                    {post.cover_image_url ? (
                                                        <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                                    ) : (
                                                        <div className="w-full h-full bg-emerald-100 flex items-center justify-center">
                                                            <Icon name="Zap" size={12} className="text-emerald-500 opacity-50" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <h5 className="text-xs font-bold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2 leading-tight mb-1">
                                                        {post.title}
                                                    </h5>
                                                    <div className="text-[10px] text-gray-400">
                                                        {post.read_time_minutes || 5} min read
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Newsletter */}
                            <div className="bg-slate-900 rounded-3xl border border-slate-800 p-8 text-white shadow-xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-indigo-500/20 transition-all duration-700" />
                                <div className="relative z-10">
                                    <h4 className="font-bold text-white text-xl mb-2 tracking-tight">Engineering Newsletter</h4>
                                    <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                                        Deep dives into scalability, systems and patterns. Join thousands of engineers on their learning journey.
                                    </p>
                                    <form onSubmit={handleNewsletterSubscribe} className="space-y-3">
                                        <div className="relative">
                                            <Icon name="Mail" size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                type="email"
                                                required
                                                value={newsletterEmail}
                                                onChange={(e) => setNewsletterEmail(e.target.value)}
                                                placeholder="Email address"
                                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={submittingNewsletter}
                                            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 text-white text-sm font-bold py-3 rounded-xl transition-all shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
                                        >
                                            {submittingNewsletter ? 'Joining...' : 'Subscribe'}
                                        </button>
                                        {subscribeStatus === 'success' && (
                                            <p className="text-emerald-400 text-[10px] text-center mt-2 font-bold">Successfully subscribed!</p>
                                        )}
                                        {subscribeStatus === 'error' && (
                                            <p className="text-rose-400 text-[10px] text-center mt-2 font-bold">Failed to subscribe.</p>
                                        )}
                                    </form>
                                    <p className="text-[10px] text-slate-500 text-center mt-4">No spam. Unsubscribe at any time.</p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
